import { useQuery } from "@tanstack/react-query";

import { PageHeader } from "../../components/shell/AppShell";
import {
  AlertBanner,
  ErrorState,
  OfflineBanner,
  Skeleton,
  StatusBadge,
  type StatusCode,
} from "../../components/ui/Feedback";
import { formatDateTime, formatRelative } from "../../lib/format";
import { systemApi } from "../../lib/api";
import { useJobEvents } from "../../lib/job-events";

const componentLabels = {
  api: "Panel API",
  database: "Yönetim veritabanı",
  queue: "İş kuyruğu",
  agent: "Agent",
  caddy: "Caddy",
} as const;

export function OverviewPage() {
  const query = useQuery({
    queryKey: ["system-overview"],
    queryFn: ({ signal }) => systemApi.overview(signal),
    refetchInterval: 30_000,
    retry: false,
  });
  const firstJobId = query.data?.activeJobs[0]?.id;
  const stream = useJobEvents(firstJobId);
  return (
    <div className="page">
      <PageHeader
        description="Platform sağlığını, etkin işleri ve dikkat gerektiren durumları izleyin."
        title="Genel Bakış"
      />
      {query.isLoading ? <Skeleton label="Sistem özeti yükleniyor" /> : null}
      {query.isError && query.data === undefined ? (
        <ErrorState
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : null}
      {query.isError && query.data !== undefined ? (
        <AlertBanner heading="Sistem durumu alınamadı" tone="warning">
          <p>Son doğrulanan bilgiler gösteriliyor. Yeniden deneyin.</p>
        </AlertBanner>
      ) : null}
      {!navigator.onLine && query.data !== undefined ? (
        <OfflineBanner updatedAt={formatDateTime(query.data.checkedAt)} />
      ) : null}
      {stream.reconnecting ? (
        <AlertBanner heading="Bağlantı yeniden kuruluyor" tone="warning">
          <p>
            Son doğrulanan işlem bilgileri korunuyor.
            {stream.lastUpdatedAt === undefined
              ? ""
              : ` Son güncelleme: ${formatDateTime(stream.lastUpdatedAt)}.`}
          </p>
        </AlertBanner>
      ) : null}
      {query.data === undefined ? null : (
        <OverviewContent data={query.data} streamConnected={stream.connected} />
      )}
    </div>
  );
}

function OverviewContent({
  data,
  streamConnected,
}: {
  readonly data: Awaited<ReturnType<typeof systemApi.overview>>;
  readonly streamConnected: boolean;
}) {
  return (
    <div className="stack">
      <section aria-label="Platform özeti" className="dashboard-grid">
        <article className="card">
          <h2>Platform sağlıklı mı?</h2>
          <StatusBadge status={data.status} />
          <p className="muted">Son kontrol: {formatDateTime(data.checkedAt)}</p>
        </article>
        <article className="card">
          <h2>Hangi işlemler çalışıyor?</h2>
          {data.activeJobs.length === 0 ? (
            <p>Şu anda çalışan işlem yok.</p>
          ) : (
            <>
              <p>{data.activeJobs.length} etkin işlem</p>
              <ul>
                {data.activeJobs.map((job) => (
                  <li key={job.id}>
                    <span className="technical">{job.kind}</span>{" "}
                    <StatusBadge status={job.status} />
                  </li>
                ))}
              </ul>
              {streamConnected ? <StatusBadge label="Canlı" status="healthy" /> : null}
            </>
          )}
        </article>
        <article className="card">
          <h2>Ne dikkat istiyor?</h2>
          {data.findings.length === 0 ? (
            <p>Tüm temel bileşenler sağlıklı.</p>
          ) : (
            <ul>
              {data.findings.map((finding) => (
                <li key={`${finding.code}-${finding.observedAt}`}>
                  <StatusBadge status={finding.severity} /> {finding.summary}
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>
      <section className="card">
        <h2>Bileşen durumu</h2>
        <ul className="stack" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {data.components.map((component) => (
            <li
              className="cluster"
              key={component.component}
              style={{ justifyContent: "space-between" }}
            >
              <span>
                <strong>{componentLabels[component.component]}</strong>
                <br />
                <span className="muted">{component.summary}</span>
              </span>
              <StatusBadge status={component.status as StatusCode} />
            </li>
          ))}
        </ul>
      </section>
      <section className="card">
        <h2>Son işlemler</h2>
        {data.recentActivity.length === 0 ? (
          <p>Henüz doğrulanmış işlem kaydı yok.</p>
        ) : (
          <ul className="stack">
            {data.recentActivity.map((activity, index) => (
              <li key={`${activity.occurredAt}-${String(index)}`}>
                <strong>{activity.action}</strong> · {activity.actorLabel} ·{" "}
                <StatusBadge status={activity.outcome === "succeeded" ? "succeeded" : "failed"} />
                <br />
                <time dateTime={activity.occurredAt}>
                  {formatDateTime(activity.occurredAt)} ({formatRelative(activity.occurredAt)})
                </time>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="card">
        <h2>Güvenlik önerileri</h2>
        {data.findings.length === 0 ? (
          <p>Şu anda uygulanabilir güvenlik önerisi yok.</p>
        ) : (
          <ul>
            {data.findings.map((finding) => (
              <li key={finding.code}>{finding.summary}</li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
